
import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent } from "@/components/ui/card";
import { Creative, Location } from "../../types";
import MediaOwnerCreatives from "../creative-upload/MediaOwnerCreatives";
import LocationCreatives from "../creative-upload/LocationCreatives";
import { useCustomMaterialLocations } from "../../hooks/useCustomMaterialLocations";

interface CreativeUploadCardProps {
  creatives: Creative[];
  locationCreatives: Record<string, Creative[]>;
  mediaOwnerCreatives: Record<string, Creative[]>;
  selectedLocations: Location[];
  uniqueMediaOwners: string[];
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveCreative: (index: number) => void;
  onLocationFileChange: (locationName: string, e: React.ChangeEvent<HTMLInputElement>, day?: number) => void;
  onRemoveLocationCreative: (locationName: string, index: number) => void;
  onUpdateLocationCreativeDays?: (locationName: string, creativeIndex: number, days: number[]) => void;
  onMediaOwnerFileChange: (mediaOwnerName: string, e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveMediaOwnerCreative: (mediaOwnerName: string, index: number) => void;
  onUpdateMediaOwnerCreativeDays?: (mediaOwnerName: string, creativeIndex: number, days: number[]) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onAreaClick: () => void;
  isUploading: boolean;
  uploadProgress?: number;
  useLocationSpecificCreatives: boolean;
  onToggleLocationSpecificCreatives: (state: boolean) => void;
  useMediaOwnerSpecificCreatives: boolean;
  onToggleMediaOwnerSpecificCreatives: (state: boolean) => void;
  onUrlSubmit?: (url: string) => void;
  onLocationUrlSubmit?: (locationName: string, url: string) => void;
  onMediaOwnerUrlSubmit?: (mediaOwnerName: string, url: string, orientation?: "portrait" | "landscape") => void;
}

const CreativeUploadCard = ({
  locationCreatives,
  mediaOwnerCreatives,
  selectedLocations,
  uniqueMediaOwners,
  onLocationFileChange,
  onRemoveLocationCreative,
  onUpdateLocationCreativeDays,
  onMediaOwnerFileChange,
  onRemoveMediaOwnerCreative,
  onUpdateMediaOwnerCreativeDays,
  isUploading,
  useLocationSpecificCreatives,
  useMediaOwnerSpecificCreatives,
  onMediaOwnerUrlSubmit,
}: CreativeUploadCardProps) => {
  const { t } = useLanguage();
  const { customMaterialLocations } = useCustomMaterialLocations(selectedLocations);

  // Avoid duplicating the special-dimension locations: they are handled by <CustomMaterialUpload />
  const customLocationNames = new Set(customMaterialLocations.map((l) => l.name));
  const standardLocations = selectedLocations.filter((l) => !customLocationNames.has(l.name));

  const hasMediaOwners = uniqueMediaOwners.length > 0;

  return (
    <Card className="overflow-hidden border-none shadow-lg hover:shadow-xl transition-all">
      <div className="h-2 bg-gradient-to-r from-primary to-secondary" />
      <CardContent className="pt-6">
        <div className="space-y-6">
          {/* Location-specific creatives (one upload per location) */}
          {useLocationSpecificCreatives && standardLocations.length > 0 && (
            <div>
              <LocationCreatives
                selectedLocations={standardLocations}
                locationCreatives={locationCreatives}
                onLocationFileChange={onLocationFileChange}
                onRemoveLocationCreative={onRemoveLocationCreative}
                onUpdateLocationCreativeDays={onUpdateLocationCreativeDays}
                isUploading={isUploading}
              />
            </div>
          )}

          {/* Per-Location Specific Creatives */}
          {hasMediaOwners && useMediaOwnerSpecificCreatives && (
            <div>
              <div className="mb-3">
                <h3 className="text-base font-medium">{t("mediaOwnerSpecificContent")}</h3>
              </div>

              <MediaOwnerCreatives
                selectedLocations={selectedLocations}
                locationCreatives={mediaOwnerCreatives}
                onLocationFileChange={onMediaOwnerFileChange}
                onRemoveLocationCreative={onRemoveMediaOwnerCreative}
                onUpdateLocationCreativeDays={onUpdateMediaOwnerCreativeDays}
                isUploading={isUploading}
                onLocationUrlSubmit={onMediaOwnerUrlSubmit}
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CreativeUploadCard;

